import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { ColorRamp, PRECIP_VIRIDIS, TEMP_TURBO } from "../../utils/colorRamps";
import { RootState } from "../store";

export type WeatherLayerType = "precip" | "tmax" | "tmean" | "tmin";
export type WeatherUnits = "imperial" | "metric";

type WeatherLayerState = {
  visible: boolean;
  layer: WeatherLayerType;
  month: number;
  opacity: number;
  colorRamp: ColorRamp;
  showTooltip: boolean;
  units: WeatherUnits;
  loading: boolean;
  syncWithFilter: boolean;
};

const initialState: WeatherLayerState = {
  visible: false,
  layer: "tmean",
  month: new Date().getMonth() + 1, // Current month (1-12)
  opacity: 0.7,
  colorRamp: TEMP_TURBO, // Use Turbo for temperature by default
  showTooltip: true,
  units: "imperial", // Default to imperial (Fahrenheit, inches)
  loading: false,
  syncWithFilter: true,
};

const weatherLayer = createSlice({
  name: "weatherLayer",
  initialState,
  reducers: {
    setWeatherVisible: (state, action: PayloadAction<boolean>) => {
      state.visible = action.payload;
    },
    toggleWeatherVisible: (state) => {
      state.visible = !state.visible;
    },
    setWeatherLayerType: (state, action: PayloadAction<WeatherLayerType>) => {
      state.layer = action.payload;
      // Auto-select color ramp based on layer type
      if (action.payload === "precip") {
        state.colorRamp = PRECIP_VIRIDIS;
      } else {
        // tmax, tmean, tmin all use Turbo
        state.colorRamp = TEMP_TURBO;
      }
    },
    setWeatherMonth: (state, action: PayloadAction<number>) => {
      state.month = action.payload;
    },
    setWeatherOpacity: (state, action: PayloadAction<number>) => {
      state.opacity = action.payload;
    },
    setWeatherColorRamp: (state, action: PayloadAction<ColorRamp>) => {
      state.colorRamp = action.payload;
    },
    setWeatherShowTooltip: (state, action: PayloadAction<boolean>) => {
      state.showTooltip = action.payload;
    },
    setWeatherUnits: (state, action: PayloadAction<WeatherUnits>) => {
      state.units = action.payload;
    },
    setWeatherLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSyncWithFilter: (state, action: PayloadAction<boolean>) => {
      state.syncWithFilter = action.payload;
    },
  },
});

export const {
  setWeatherVisible,
  toggleWeatherVisible,
  setWeatherLayerType,
  setWeatherMonth,
  setWeatherOpacity,
  setWeatherColorRamp,
  setWeatherShowTooltip,
  setWeatherUnits,
  setWeatherLoading,
  setSyncWithFilter,
} = weatherLayer.actions;

export default weatherLayer.reducer;

// Selectors
const selectWeatherVisible = (state: RootState) => state.weatherLayer.visible;
const selectWeatherLayerType = (state: RootState) => state.weatherLayer.layer;
const selectWeatherMonth = (state: RootState) => state.weatherLayer.month;
const selectWeatherOpacity = (state: RootState) => state.weatherLayer.opacity;
const selectWeatherColorRamp = (state: RootState) =>
  state.weatherLayer.colorRamp;

const selectWeatherShowTooltip = (state: RootState) =>
  state.weatherLayer.showTooltip;

const selectWeatherUnits = (state: RootState) => state.weatherLayer.units;

const selectWeatherLoading = (state: RootState) => state.weatherLayer.loading;

const selectSyncWithFilter = (state: RootState) =>
  state.weatherLayer.syncWithFilter;

export const useWeatherVisible = () => {
  return useSelector(selectWeatherVisible);
};

export const useWeatherLayerType = () => {
  return useSelector(selectWeatherLayerType);
};

export const useWeatherMonth = () => {
  return useSelector(selectWeatherMonth);
};

export const useWeatherOpacity = () => {
  return useSelector(selectWeatherOpacity);
};

export const useWeatherColorRamp = () => {
  return useSelector(selectWeatherColorRamp);
};

export const useWeatherShowTooltip = () => {
  return useSelector(selectWeatherShowTooltip);
};

export const useWeatherUnits = () => {
  return useSelector(selectWeatherUnits);
};

export const useWeatherLoading = () => {
  return useSelector(selectWeatherLoading);
};

export const useSyncWithFilter = () => {
  return useSelector(selectSyncWithFilter);
};
